<script lang="ts">
	import { Text, Input, Button, Alert } from '@Shelly/shelly-design-system';
	import { _ } from 'svelte-i18n';
	import { i18nKeyForError, type ErrorCode } from '../lib/errors';
	import { messageForKey } from '../lib/messages';

	interface Props {
		email: string;
		password: string;
		loading: boolean;
		error: ErrorCode | null;
		fatalReason: 'missing_redirect_uri' | 'missing_client_id' | null;
		providerName?: string;
		onsubmit: () => void;
	}

	let {
		email = $bindable(''),
		password = $bindable(''),
		loading,
		error,
		fatalReason,
		providerName,
		onsubmit,
	}: Props = $props();

	// Mirrors shelly-app-3's Login.svelte: the form lives inside an SPA that
	// owns /signup, /request-password-reset, /terms-and-conditions, etc.
	// We don't have those routes (OAuth handshake is single-page), so the
	// equivalents link out to control.shelly.cloud where they're hosted.
	const FORGOT_URL = 'https://control.shelly.cloud/#/request-password-reset';
	const SIGNUP_URL = 'https://control.shelly.cloud/#/signup';
	const TERMS_URL = 'https://control.shelly.cloud/#/terms-and-conditions';
	const PRIVACY_URL = 'https://control.shelly.cloud/#/privacy-policy';
	const DATA_ACT_URL = 'https://control.shelly.cloud/#/data-act';
	const CHANGELOG_URL = 'https://control.shelly.cloud/#/changelog';

	let mailError = $state<string>('');
	let passError = $state<string>('');

	function capFirstChar(s: string): string {
		if (!s) return s;
		return s.charAt(0).toUpperCase() + s.slice(1);
	}

	function validateEmailFormat(value: string): boolean {
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
	}

	function clearErrors() {
		mailError = '';
		passError = '';
	}

	function validate(): boolean {
		if (!email) {
			mailError = messageForKey('label_enter_email');
			return false;
		}
		if (!validateEmailFormat(email)) {
			mailError = messageForKey('input_error_valid_email');
			return false;
		}
		if (!password) {
			passError = messageForKey('label_enter_password');
			return false;
		}
		return true;
	}

	function handleLogin() {
		clearErrors();
		if (!validate()) return;
		onsubmit();
	}

	function onInputEmail(e: Event) {
		email = (e.target as HTMLInputElement).value;
	}

	function onInputPass(e: Event) {
		const target = e.target as HTMLInputElement;
		password = target.value;
		// Mirrors app-3: blur after change prevents iOS keyboard resize jitter.
		target.blur();
	}

	function openForgot() {
		if (loading) return;
		window.location.href = FORGOT_URL;
	}

	function openSignup() {
		if (loading || fatalReason) return;
		window.location.href = SIGNUP_URL;
	}
</script>

<Text element="h1" class="mb-l">{$_('login')}</Text>



{#if fatalReason}
	<Alert
		type="danger"
		heading={$_('error_heading')}
		message={$_('error_wrong_parameters')}
		class="mb-m"
	/>
{:else if error}
	<Alert
		type="danger"
		heading={$_('error_heading')}
		message={$_(i18nKeyForError(error))}
		class="mb-m"
	/>
{/if}

<Input
	id="input-main-email"
	aria-labelledby="input-main-email"
	bind:value={email}
	size="l"
	class="mb-m"
	label={capFirstChar($_('placeholder_email'))}
	icon="fa-solid fa-envelope"
	type="text"
	inputmode="email"
	error={mailError}
	on:click={clearErrors}
	on:enter={handleLogin}
	on:change={onInputEmail}
	disabled={loading || !!fatalReason}
/>

<Input
	id="input-main-password"
	aria-labelledby="input-main-password"
	bind:value={password}
	size="l"
	class="mb-l"
	label={capFirstChar($_('placeholder_password'))}
	icon="fa-solid fa-lock-keyhole"
	type="password"
	error={passError}
	on:click={clearErrors}
	on:change={onInputPass}
	on:enter={handleLogin}
	disabled={loading || !!fatalReason}
	a11yProps={{ iconButtonAriaLabel: $_('button_show_password') }}
/>

<!-- <Text
	element="div"
	class={`mb-m d-flex link-styles text-small${loading ? ' disabled' : ''}`}
	on:click={openForgot}
>
	{$_('forgot_password')}
</Text> -->

<Button
	width="full"
	class="mb-m"
	disabled={loading || !!fatalReason}
	{loading}
	on:click={handleLogin}
>
	{$_('login')}
</Button>

{#if providerName}
	<Text class="mb-m text-small">
		{$_('provider_intro', { default: 'By signing in, you are authorizing {provider} to control your devices.', values: { provider: providerName } })}
	</Text>
{/if}

<Text class="text-small">
	{$_('text_by_logging')}
	<a class="text-small link-styles" target="_blank" rel="noopener" href={TERMS_URL}>
		{$_('shelly_terms_and_conditions')}
	</a>,
	<a class="text-small link-styles" target="_blank" rel="noopener" href={PRIVACY_URL}>
		{$_('privacy')}
	</a>
	{$_('and')}
	<a class="text-small link-styles" target="_blank" rel="noopener" href={DATA_ACT_URL}>
		{$_('data_act')}
	</a>.
</Text>

<Text class="mt-s text-small">
	{$_('change_log_intro')}
	<a class="text-small link-styles" target="_blank" rel="noopener" href={CHANGELOG_URL}>
		{capFirstChar($_('shelly_change_log'))}
	</a>.
</Text>

<hr class="mt-xl mb-xl" />

<Button
	width="full"
	variant="secondary"
	disabled={loading || !!fatalReason}
	on:click={openSignup}
>
	{$_('create_account')}
</Button>

<style>
	:global(.link-styles.disabled) {
		color: var(--color-3);
		cursor: not-allowed;
		pointer-events: none;
	}
	:global(a.link-styles),
	:global(a.link-styles:visited) {
		text-decoration: none;
	}
	:global(a.link-styles:hover) {
		text-decoration: underline;
	}
	/* Provider intro banner (Google entry). Mirrors the legacy
	   oauth_login_google.html "By signing in, you are authorizing Google
	   to control your devices." line above the form. */
	:global(.provider-intro) {
		color: var(--color-2, currentColor);
		text-align: center;
		opacity: 0.9;
	}
</style>
